import { differenceInMonths, parseISO } from 'date-fns';
import { HomeInspectionRule } from './types';

const AGENCY_OVERRIDE_CODES = [
  50005, 50008, 50009, 50010, 50051, 50055, 50096, 50097, 50098, 50101, 50105, 50106, 50014, 50116
];

export const homeInspectionRules: HomeInspectionRule[] = [
  {
    name: 'state_mi',
    description: 'Order inspection if state is MI',
    evaluate: (input) => input.offer.quote.home.homeLocation.state === 'MI',
    getValue: (input) => input.offer.quote.home.homeLocation.state,
    getThreshold: () => 'MI'
  },

  {
    name: 'state_wv',
    description: 'Order inspection if state is WV',
    evaluate: (input) => input.offer.quote.home.homeLocation.state === 'WV',
    getValue: (input) => input.offer.quote.home.homeLocation.state,
    getThreshold: () => 'WV'
  },

  {
    name: 'fpc_8b_or_9',
    description: 'Order inspection if FPC is 8B or 9',
    evaluate: (input) => ['8B', '9'].includes(input.offer.quote.home.fpc),
    getValue: (input) => input.offer.quote.home.fpc,
    getThreshold: () => ['8B', '9']
  },

  {
    name: 'next_day_effective',
    description: 'Order inspection if effective date is next day',
    evaluate: (input) => {
      const effectiveDate = parseISO(input.offer.quote.global.homeEffectiveDate);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      return effectiveDate.toDateString() === tomorrow.toDateString();
    },
    getValue: (input) => input.offer.quote.global.homeEffectiveDate,
    getThreshold: () => 'Next day'
  },

  {
    name: 'agency_override_codes',
    description: 'Order inspection for specific agency override codes',
    evaluate: (input) => {
      const agencyRep = input.offer.quote.rep && input.offer.quote.rep.includes('_');
      const overrideCodesAddedByAgency = input.offer.overrides?.overridesForUW?.map(({ code }) => code) || [];

      return (
        overrideCodesAddedByAgency?.length > 0 &&
        Boolean(agencyRep) &&
        AGENCY_OVERRIDE_CODES.some((agencyCode) => overrideCodesAddedByAgency.includes(agencyCode))
      );
    },
    getValue: (input) => input.offer.overrides?.overridesForUW || [],
    getThreshold: () => AGENCY_OVERRIDE_CODES
  },

  {
    name: 'small_home_sqft',
    description: 'Order inspection if home is less than 1300 sq ft',
    evaluate: (input) => input.offer.quote.home.sqFt < 1300,
    getValue: (input) => input.offer.quote.home.sqFt,
    getThreshold: () => 1300
  },

  {
    name: 'old_home_year_built',
    description: 'Order inspection if year built is < 1970',
    evaluate: (input) => input.offer.quote.home.yearBuilt < 1970,
    getValue: (input) => input.offer.quote.home.yearBuilt,
    getThreshold: () => 1970
  },

  {
    name: 'old_shingle_roof',
    description: 'Order inspection if roof is shingle and > 10 years old',
    evaluate: (input) => {
      const { roofType, roofYear } = input.offer.quote.home;
      const currentYear = new Date().getFullYear();
      const roofAge = currentYear - roofYear;

      // Check for shingle roof types based on lookup IDs
      const shingleRoofTypes = ['AF', 'C']; // AF = Composition/Asphalt/Fiberglass, C = Composition
      const isShingleRoof = shingleRoofTypes.includes(roofType);

      return isShingleRoof && roofAge > 10;
    },
    getValue: (input) => {
      const currentYear = new Date().getFullYear();
      return {
        roofType: input.offer.quote.home.roofType,
        roofAge: currentYear - input.offer.quote.home.roofYear
      };
    },
    getThreshold: () => ({ roofAge: 10, roofType: 'shingle/AF/C' })
  },

  {
    name: 'high_coverage_b_pct',
    description: 'Order inspection if Coverage B > 30% of Coverage A',
    evaluate: (input) => {
      const coverageBPct = input.offer.quote.homeCoverage.coverageBPctOfA || 0;
      return coverageBPct > 30;
    },
    getValue: (input) => input.offer.quote.homeCoverage.coverageBPctOfA || 0,
    getThreshold: () => 30
  },

  {
    name: 'low_coverage_c_pct',
    description: 'Order inspection if Coverage C < 30% of Coverage A',
    evaluate: (input) => {
      const coverageCPct = input.offer.quote.homeCoverage.coverageCPctOfA || 0;
      return coverageCPct < 30;
    },
    getValue: (input) => input.offer.quote.homeCoverage.coverageCPctOfA || 0,
    getThreshold: () => 30
  },

  {
    name: 'high_coverage_a',
    description: 'Order inspection if Coverage A > $950k',
    evaluate: (input) => input.offer.quote.homeCoverage.coverageA > 950000,
    getValue: (input) => input.offer.quote.homeCoverage.coverageA,
    getThreshold: () => 950000
  },

  {
    name: 'low_coverage_a',
    description: 'Order inspection if Coverage A < $225k',
    evaluate: (input) => input.offer.quote.homeCoverage.coverageA < 225000,
    getValue: (input) => input.offer.quote.homeCoverage.coverageA,
    getThreshold: () => 225000
  },

  {
    name: 'home_claims_history',
    description: 'Order inspection if homeClaimCount >= 1 (violations D+E+F+G)',
    evaluate: (input) => {
      const { homeViolations } = input.offer.quote;
      const homeClaimCount = homeViolations.D + homeViolations.E + homeViolations.F + homeViolations.G;
      return homeClaimCount >= 1;
    },
    getValue: (input) => {
      const { homeViolations } = input.offer.quote;
      return homeViolations.D + homeViolations.E + homeViolations.F + homeViolations.G;
    },
    getThreshold: () => 1
  },

  {
    name: 'not_new_purchase_low_insurance_score',
    description: 'Order inspection if not new purchase and any driver has insurance score 26-50',
    evaluate: (input) => {
      const { purchaseDate } = input.offer.quote.home;
      const effectiveDate = parseISO(input.offer.quote.global.homeEffectiveDate);

      // Check if it's a new purchase (purchase date within 12 months of effective date)
      let isNewPurchase = false;
      if (purchaseDate) {
        const purchaseDateParsed = parseISO(purchaseDate);
        const monthsDiff = Math.abs(differenceInMonths(effectiveDate, purchaseDateParsed));
        isNewPurchase = monthsDiff <= 12;
      }

      // If it's a new purchase, this rule doesn't apply
      if (isNewPurchase) {
        return false;
      }

      // Check insurance scores of all drivers
      const drivers = input.offer.quote.drivers || [];
      return drivers.some((driver) => {
        const score = driver.insuranceScore?.score;
        return score !== undefined && score >= 26 && score <= 50;
      });
    },
    getValue: (input) => {
      const { purchaseDate } = input.offer.quote.home;
      const effectiveDate = parseISO(input.offer.quote.global.homeEffectiveDate);
      const drivers = input.offer.quote.drivers || [];

      let isNewPurchase = false;
      if (purchaseDate) {
        const purchaseDateParsed = parseISO(purchaseDate);
        const monthsDiff = Math.abs(differenceInMonths(effectiveDate, purchaseDateParsed));
        isNewPurchase = monthsDiff <= 12;
      }

      const driverScores = drivers.map((driver) => driver.insuranceScore?.score).filter((score) => score !== undefined);

      return {
        isNewPurchase,
        driverInsuranceScores: driverScores,
        monthsSincePurchase: purchaseDate ? Math.abs(differenceInMonths(effectiveDate, parseISO(purchaseDate))) : null
      };
    },
    getThreshold: () => ({ minScore: 26, maxScore: 50, maxMonthsSincePurchase: 12 })
  },

  {
    name: 'new_purchase_high_replacement_cost',
    description: 'Order inspection if new purchase and replacement cost >= 150% of purchase price',
    evaluate: (input) => {
      const { purchaseDate, replacementCostEstimate, purchaseAmount } = input.offer.quote.home;
      const effectiveDate = parseISO(input.offer.quote.global.homeEffectiveDate);

      // Must be a new purchase with purchase price available
      if (!purchaseDate || !purchaseAmount || !replacementCostEstimate?.total) {
        return false;
      }

      const purchaseDateParsed = parseISO(purchaseDate);
      const monthsDiff = Math.abs(differenceInMonths(effectiveDate, purchaseDateParsed));
      const isNewPurchase = monthsDiff <= 12;

      if (!isNewPurchase) {
        return false;
      }

      const ratio = replacementCostEstimate.total / purchaseAmount;
      return ratio >= 1.5; // 150%
    },
    getValue: (input) => {
      const { replacementCostEstimate, purchaseAmount } = input.offer.quote.home;
      if (!replacementCostEstimate?.total || !purchaseAmount) {
        return null;
      }

      return {
        replacementCost: replacementCostEstimate.total,
        purchasePrice: purchaseAmount,
        ratio: replacementCostEstimate.total / purchaseAmount
      };
    },
    getThreshold: () => 1.5
  }
];
